import type { ShopCatalogProduct } from "./templateShopCatalog";

/**
 * Parsed `{username}.breadcrumbs`.
 * Format: `Primary | secondary | tertiary | brand`
 *
 * The category directory uses **primary only**. Secondary and tertiary are
 * drill-down; brand is the fourth segment only — never backfilled from
 * secondary/tertiary when missing.
 */
export type ParsedBreadcrumbs = {
  primary: string;
  primarySlug: string;
  secondary: string;
  secondarySlug: string;
  sub: string;
  subSlug: string;
  brand: string;
  brandSlug: string;
};

export type BreadcrumbNavItem = {
  label: string;
  slug: string;
  count: number;
};

export type ShopBrowseIndex = {
  primaries: BreadcrumbNavItem[];
  secondariesByPrimarySlug: Record<string, BreadcrumbNavItem[]>;
  /** Third crumb (tertiary) keyed by `primary|secondary`. */
  subsByPathSlug: Record<string, BreadcrumbNavItem[]>;
  /** Fourth crumb (brand) keyed by `primary|secondary|sub`. */
  brandsByPathSlug: Record<string, BreadcrumbNavItem[]>;
};

export type ShopBrowseSelection = {
  primarySlug?: string | null;
  secondarySlug?: string | null;
  /** Category subcategory / third browse crumb. */
  subSlug?: string | null;
  /** Product brand / fourth browse crumb. */
  brandSlug?: string | null;
  /** Multi-brand filter (Featured brands). When set, products match any slug. */
  brandSlugs?: string[] | null;
};

/** Slug for uncategorized products, missing subcategories, or missing brands. */
export const SHOP_BROWSE_OTHER_BRAND_SLUG = "other";
export const SHOP_BROWSE_OTHER_SLUG = SHOP_BROWSE_OTHER_BRAND_SLUG;
export const SHOP_BROWSE_OTHER_LABEL = "Other";

type SlugCountBucket = Map<string, { label: string; count: number }>;

function normalizeBrowseSlug(
  slug: string | null | undefined,
): string {
  return String(slug ?? "").trim().toLowerCase();
}

function productHasBrowseBrand(bc: ParsedBreadcrumbs): boolean {
  return Boolean(String(bc.brand ?? "").trim() && String(bc.brandSlug ?? "").trim());
}

function productHasBrowsePrimary(bc: ParsedBreadcrumbs | null | undefined): boolean {
  return Boolean(bc?.primary?.trim() && bc.primarySlug?.trim());
}

function productHasBrowseSecondary(bc: ParsedBreadcrumbs | null | undefined): boolean {
  return Boolean(bc?.secondary?.trim() && bc.secondarySlug?.trim());
}

function productHasBrowseSub(bc: ParsedBreadcrumbs | null | undefined): boolean {
  return Boolean(bc?.sub?.trim() && bc.subSlug?.trim());
}

/** Third browse crumb = tertiary segment only (never brand). */
function productHasBrowseThird(bc: ParsedBreadcrumbs | null | undefined): boolean {
  return productHasBrowseSub(bc);
}

function breadcrumbLabelOrSlug(label: string, slug: string): string {
  const labelText = String(label || "").trim();
  if (labelText) return labelText;
  return String(slug || "")
    .trim()
    .replace(/-/g, " ");
}

function productBrowseThirdLabel(bc: ParsedBreadcrumbs): string {
  return breadcrumbLabelOrSlug(bc.sub, bc.subSlug);
}

function productBrowseThirdSlug(bc: ParsedBreadcrumbs): string {
  return bc.subSlug;
}

export function slugifyBreadcrumbLabel(label: string): string {
  return String(label || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Primary slug used by browse nav + product filters. */
export function productBrowsePrimarySlug(
  bc: ParsedBreadcrumbs | null | undefined,
): string {
  if (!bc) return "";
  return slugifyBreadcrumbLabel(bc.primary || "");
}

function withSlugs(parts: {
  primary: string;
  secondary: string;
  sub: string;
  brand: string;
}): ParsedBreadcrumbs {
  return {
    primary: parts.primary,
    secondary: parts.secondary,
    sub: parts.sub,
    brand: parts.brand,
    primarySlug: slugifyBreadcrumbLabel(parts.primary),
    secondarySlug: slugifyBreadcrumbLabel(parts.secondary),
    subSlug: slugifyBreadcrumbLabel(parts.sub),
    brandSlug: slugifyBreadcrumbLabel(parts.brand),
  };
}

/**
 * Scraper / storefront chrome that shows up as a "category" when Amazon, LTK,
 * or ShopMy nav HTML is copied into `{username}.breadcrumbs`. Shifted off the
 * primary slot so they never become directory links.
 */
const NOISE_BREADCRUMB_LABELS = new Set([
  "amazon",
  "amazon.com",
  "back",
  "back to results",
  "breadcrumb",
  "breadcrumbs",
  "shop all",
  "see all",
  "see all results",
  "skip to main content",
  "skip to results",
]);

function isNoiseBreadcrumbLabel(label: string): boolean {
  const n = label.trim().toLowerCase();
  if (!n) return true;
  if (NOISE_BREADCRUMB_LABELS.has(n)) return true;
  if (n === "products" || n === "target") return true;
  if (n.startsWith("shop all")) return true;
  if (n.startsWith("translation missing")) return true;
  return n.startsWith("back to ");
}

function shiftNoiseBreadcrumbParts(parts: {
  primary: string;
  secondary: string;
  sub: string;
  brand: string;
}): { primary: string; secondary: string; sub: string; brand: string } {
  let { primary, secondary, sub, brand } = parts;
  while (primary && isNoiseBreadcrumbLabel(primary)) {
    primary = secondary;
    secondary = sub;
    sub = brand;
    brand = "";
  }
  if (secondary && isNoiseBreadcrumbLabel(secondary)) {
    secondary = sub;
    sub = brand;
    brand = "";
  }
  if (sub && isNoiseBreadcrumbLabel(sub)) {
    sub = brand;
    brand = "";
  }
  if (brand && isNoiseBreadcrumbLabel(brand)) {
    brand = "";
  }
  return { primary, secondary, sub, brand };
}

/** Split a breadcrumb path. Never split on commas — department names contain them. */
function splitBreadcrumbPath(value: string): string[] {
  if (value.includes("|")) {
    return value
      .split(/\s*\|\s*/)
      .map((p) => p.trim())
      .filter(Boolean);
  }
  if (value.includes(">")) {
    return value
      .split(/\s*>\s*/)
      .map((p) => p.trim())
      .filter(Boolean);
  }
  return [value];
}

/**
 * Parse `{username}.breadcrumbs` as positional
 * `Primary | secondary | tertiary | brand`.
 *
 * Missing segments stay empty. Brand is **only** the 4th segment — never
 * filled from tertiary/secondary when the row has fewer than 4 parts.
 * Comma-separated labels (e.g. "Clothing, Shoes & Jewelry") are not split.
 */
export function parseProductBreadcrumbs(
  raw: string | null | undefined,
): ParsedBreadcrumbs | null {
  const value = String(raw ?? "").trim();
  if (!value) return null;

  const parts = splitBreadcrumbPath(value);
  let primary = parts[0] ?? "";
  let secondary = parts[1] ?? "";
  let sub = parts[2] ?? "";
  let brand = parts[3] ?? "";

  ({ primary, secondary, sub, brand } = shiftNoiseBreadcrumbParts({
    primary,
    secondary,
    sub,
    brand,
  }));

  if (!primary) return null;

  return withSlugs({ primary, secondary, sub, brand });
}

export function formatShopBrowseCount(count: number): string {
  if (count >= 1000) {
    const k = count / 1000;
    const rounded = k >= 10 ? Math.round(k) : Math.round(k * 10) / 10;
    return `${String(rounded).replace(/\.0$/, "")}k`;
  }
  return String(count);
}

/** Count by slug so `Jewelry` + `JEWELRY` merge into one pill. */
function bumpSlugCount(
  map: SlugCountBucket,
  label: string,
  slug: string,
) {
  const slugKey = String(slug || "").trim().toLowerCase();
  const labelText = String(label || "").trim();
  if (!slugKey || !labelText) return;

  const existing = map.get(slugKey);
  if (existing) {
    existing.count += 1;
    if (labelText.length > existing.label.length) {
      existing.label = labelText;
    }
  } else {
    map.set(slugKey, { label: labelText, count: 1 });
  }
}

function slugMapToNavItems(map: SlugCountBucket): BreadcrumbNavItem[] {
  return [...map.entries()]
    .sort(
      (a, b) =>
        b[1].count - a[1].count || a[1].label.localeCompare(b[1].label),
    )
    .map(([slug, { label, count }]) => ({
      label,
      slug,
      count,
    }));
}

/** Aggregate primary / secondary / sub / brand counts from catalog rows.
 *  Products with no breadcrumb primary are omitted — no synthetic "Other". */
export function buildShopBrowseIndex(
  products: ShopCatalogProduct[],
): ShopBrowseIndex {
  const primaryCounts: SlugCountBucket = new Map();
  const secondaryCounts = new Map<string, SlugCountBucket>();
  const subCounts = new Map<string, SlugCountBucket>();
  const brandCounts = new Map<string, SlugCountBucket>();

  for (const product of products) {
    const bc = product.breadcrumbs;
    if (!productHasBrowsePrimary(bc)) continue;

    // Categories come from the stored breadcrumb primary.
    const primarySlugKey = productBrowsePrimarySlug(bc);
    if (!primarySlugKey) continue;
    const primaryLabel = String(bc!.primary || "").trim();
    if (!primaryLabel) continue;

    bumpSlugCount(primaryCounts, primaryLabel, primarySlugKey);

    if (!productHasBrowseSecondary(bc)) continue;

    if (!secondaryCounts.has(primarySlugKey)) {
      secondaryCounts.set(primarySlugKey, new Map());
    }
    bumpSlugCount(
      secondaryCounts.get(primarySlugKey)!,
      breadcrumbLabelOrSlug(bc!.secondary, bc!.secondarySlug),
      bc!.secondarySlug,
    );

    const pathKey = `${primarySlugKey}|${bc!.secondarySlug}`;
    if (!productHasBrowseThird(bc)) continue;

    if (!subCounts.has(pathKey)) {
      subCounts.set(pathKey, new Map());
    }
    bumpSlugCount(
      subCounts.get(pathKey)!,
      productBrowseThirdLabel(bc!),
      productBrowseThirdSlug(bc!),
    );

    // Fourth crumb: brand only when the 4th segment is present — never invent one.
    if (!productHasBrowseSub(bc) || !productHasBrowseBrand(bc!)) continue;
    const brandPathKey = `${pathKey}|${bc!.subSlug}`;
    if (!brandCounts.has(brandPathKey)) {
      brandCounts.set(brandPathKey, new Map());
    }
    bumpSlugCount(
      brandCounts.get(brandPathKey)!,
      breadcrumbLabelOrSlug(bc!.brand, bc!.brandSlug),
      bc!.brandSlug,
    );
  }

  const secondariesByPrimarySlug: Record<string, BreadcrumbNavItem[]> = {};
  for (const [primarySlug, map] of secondaryCounts.entries()) {
    secondariesByPrimarySlug[primarySlug] = slugMapToNavItems(map);
  }

  const subsByPathSlug: Record<string, BreadcrumbNavItem[]> = {};
  for (const [pathKey, map] of subCounts.entries()) {
    subsByPathSlug[pathKey] = slugMapToNavItems(map);
  }

  const brandsByPathSlug: Record<string, BreadcrumbNavItem[]> = {};
  for (const [pathKey, map] of brandCounts.entries()) {
    brandsByPathSlug[pathKey] = slugMapToNavItems(map);
  }

  return {
    primaries: slugMapToNavItems(primaryCounts),
    secondariesByPrimarySlug,
    subsByPathSlug,
    brandsByPathSlug,
  };
}

export function findBreadcrumbLabelBySlug(
  items: BreadcrumbNavItem[],
  slug: string | null | undefined,
): string | null {
  const want = String(slug ?? "").trim().toLowerCase();
  if (!want) return null;
  return items.find((i) => i.slug === want)?.label ?? null;
}

/** Normalize browse selection slugs. Brand is never promoted into tertiary. */
export function coerceShopBrowseSelection(
  selection: ShopBrowseSelection,
): {
  primarySlug: string;
  secondarySlug: string;
  subSlug: string;
  brandSlug: string;
} {
  return {
    primarySlug: normalizeBrowseSlug(selection.primarySlug),
    secondarySlug: normalizeBrowseSlug(selection.secondarySlug),
    subSlug: normalizeBrowseSlug(selection.subSlug),
    brandSlug: normalizeBrowseSlug(selection.brandSlug),
  };
}

function findLabelFromCatalog(
  catalog: ShopCatalogProduct[],
  level: "primary" | "secondary" | "sub" | "brand",
  slug: string,
  primarySlug?: string,
  secondarySlug?: string,
  subSlug?: string,
): string | null {
  if (!slug) return null;
  for (const row of catalog) {
    const bc = row.breadcrumbs;
    if (!bc?.primary) continue;
    if (level === "primary" && bc.primarySlug === slug) return bc.primary;
    if (
      level === "secondary" &&
      bc.secondarySlug === slug &&
      (!primarySlug || bc.primarySlug === primarySlug)
    ) {
      return bc.secondary;
    }
    if (
      level === "sub" &&
      slug === SHOP_BROWSE_OTHER_BRAND_SLUG &&
      (!primarySlug || bc.primarySlug === primarySlug) &&
      (!secondarySlug || bc.secondarySlug === secondarySlug) &&
      !productHasBrowseThird(bc)
    ) {
      return SHOP_BROWSE_OTHER_LABEL;
    }
    if (
      level === "sub" &&
      productHasBrowseThird(bc) &&
      productBrowseThirdSlug(bc) === slug &&
      (!primarySlug || bc.primarySlug === primarySlug) &&
      (!secondarySlug || bc.secondarySlug === secondarySlug)
    ) {
      return productBrowseThirdLabel(bc);
    }
    if (
      level === "brand" &&
      slug === SHOP_BROWSE_OTHER_BRAND_SLUG &&
      (!primarySlug || bc.primarySlug === primarySlug) &&
      (!secondarySlug || bc.secondarySlug === secondarySlug) &&
      (!subSlug || productBrowseThirdSlug(bc) === subSlug) &&
      productHasBrowseSub(bc) &&
      !productHasBrowseBrand(bc)
    ) {
      return SHOP_BROWSE_OTHER_LABEL;
    }
    if (
      level === "brand" &&
      productHasBrowseSub(bc) &&
      productHasBrowseBrand(bc) &&
      bc.brandSlug === slug &&
      (!primarySlug || bc.primarySlug === primarySlug) &&
      (!secondarySlug || bc.secondarySlug === secondarySlug) &&
      (!subSlug || bc.subSlug === subSlug)
    ) {
      return breadcrumbLabelOrSlug(bc.brand, bc.brandSlug);
    }
  }
  return null;
}

/** Resolve slug → label from index, then scan catalog rows as fallback. */
export function resolveShopBrowseLabels(
  index: ShopBrowseIndex,
  selection: ShopBrowseSelection,
  catalog: ShopCatalogProduct[] = [],
): {
  primary: string | null;
  secondary: string | null;
  sub: string | null;
  brand: string | null;
} {
  const { primarySlug, secondarySlug, subSlug, brandSlug } =
    coerceShopBrowseSelection(selection);

  const primary =
    (primarySlug === SHOP_BROWSE_OTHER_SLUG ? SHOP_BROWSE_OTHER_LABEL : null) ??
    findBreadcrumbLabelBySlug(index.primaries, primarySlug) ??
    findLabelFromCatalog(catalog, "primary", primarySlug);

  let secondary: string | null = null;
  let sub: string | null = null;
  let brand: string | null = null;

  if (primarySlug && primarySlug !== SHOP_BROWSE_OTHER_SLUG) {
    const secondaries = index.secondariesByPrimarySlug[primarySlug] ?? [];
    secondary =
      (secondarySlug === SHOP_BROWSE_OTHER_SLUG ? SHOP_BROWSE_OTHER_LABEL : null) ??
      findBreadcrumbLabelBySlug(secondaries, secondarySlug) ??
      findLabelFromCatalog(catalog, "secondary", secondarySlug, primarySlug);

    if (secondarySlug) {
      const pathKey = `${primarySlug}|${secondarySlug}`;
      const subs = index.subsByPathSlug[pathKey] ?? [];
      if (subSlug === SHOP_BROWSE_OTHER_BRAND_SLUG) {
        sub = SHOP_BROWSE_OTHER_LABEL;
      } else {
        sub =
          findBreadcrumbLabelBySlug(subs, subSlug) ??
          findLabelFromCatalog(
            catalog,
            "sub",
            subSlug,
            primarySlug,
            secondarySlug,
          );
      }

      if (subSlug) {
        const brandPathKey = `${pathKey}|${subSlug}`;
        const brands = index.brandsByPathSlug[brandPathKey] ?? [];
        if (brandSlug === SHOP_BROWSE_OTHER_BRAND_SLUG) {
          brand = SHOP_BROWSE_OTHER_LABEL;
        } else {
          brand =
            findBreadcrumbLabelBySlug(brands, brandSlug) ??
            findLabelFromCatalog(
              catalog,
              "brand",
              brandSlug,
              primarySlug,
              secondarySlug,
              subSlug,
            );
        }
      }
    }
  }

  // Featured brand /brand/:slug deep-links have no primary path — still resolve
  // a label so ShopBrowseNav can show a clearable crumb.
  if (!brand && brandSlug) {
    if (brandSlug === SHOP_BROWSE_OTHER_BRAND_SLUG) {
      brand = SHOP_BROWSE_OTHER_LABEL;
    } else {
      brand =
        findLabelFromCatalog(catalog, "brand", brandSlug) ??
        findBrandLabelBySlugAnywhere(catalog, brandSlug);
    }
  }

  return { primary, secondary, sub, brand };
}

/** Brand label from any catalog row with this brandSlug (ignores path). */
function findBrandLabelBySlugAnywhere(
  catalog: ShopCatalogProduct[],
  slug: string,
): string | null {
  const want = String(slug || "").trim().toLowerCase();
  if (!want) return null;
  for (const row of catalog) {
    const bc = row.breadcrumbs;
    if (!bc || !productHasBrowseBrand(bc)) continue;
    if (String(bc.brandSlug || "").trim().toLowerCase() !== want) continue;
    return breadcrumbLabelOrSlug(bc.brand, bc.brandSlug);
  }
  return null;
}

/**
 * Affiliate taxonomies often use `Women's | Bags | Clutches` while curated
 * shop rows use primary `Bags & Purses`. Treat Women's→Bags as Bags & Purses
 * when browsing that primary (no secondary drill-down yet).
 */
export function productMatchesBagsPursesPrimaryAlias(
  bc: ParsedBreadcrumbs | null | undefined,
): boolean {
  if (!bc) return false;
  return (
    productBrowsePrimarySlug(bc) === "womens" &&
    String(bc.secondarySlug || "").trim().toLowerCase() === "bags"
  );
}

/** Same rules as index counts — used for grid filtering. */
export function productMatchesBrowseSelection(
  product: ShopCatalogProduct,
  selection: ShopBrowseSelection,
): boolean {
  const bc = product.breadcrumbs;
  const { primarySlug, secondarySlug, subSlug, brandSlug } =
    coerceShopBrowseSelection(selection);

  if (primarySlug === SHOP_BROWSE_OTHER_SLUG) {
    return !productHasBrowsePrimary(bc);
  }

  if (!productHasBrowsePrimary(bc)) return false;

  if (primarySlug && productBrowsePrimarySlug(bc) !== primarySlug) {
    // Bags & Purses root: also include Women's | Bags | … affiliate crumbs.
    if (
      primarySlug === "bags-purses" &&
      !secondarySlug &&
      !subSlug &&
      !brandSlug &&
      productMatchesBagsPursesPrimaryAlias(bc)
    ) {
      return true;
    }
    return false;
  }

  if (secondarySlug === SHOP_BROWSE_OTHER_SLUG) {
    return Boolean(primarySlug) && !productHasBrowseSecondary(bc);
  }

  if (secondarySlug && bc!.secondarySlug !== secondarySlug) return false;

  // Third crumb: tertiary segment only.
  if (subSlug) {
    if (!primarySlug || !secondarySlug) return false;
    if (subSlug === SHOP_BROWSE_OTHER_BRAND_SLUG) {
      if (productHasBrowseThird(bc)) return false;
    } else if (
      !productHasBrowseThird(bc) ||
      productBrowseThirdSlug(bc!) !== subSlug
    ) {
      return false;
    }
  }

  // Fourth crumb: real product brand (only when a sub is selected).
  if (brandSlug) {
    if (!primarySlug || !secondarySlug || !subSlug) return false;
    if (!productHasBrowseSub(bc) || bc!.subSlug !== subSlug) return false;
    if (brandSlug === SHOP_BROWSE_OTHER_BRAND_SLUG) {
      return !productHasBrowseBrand(bc!);
    }
    if (!productHasBrowseBrand(bc!) || bc!.brandSlug !== brandSlug) {
      return false;
    }
  }

  return true;
}

/** Live count for current selection — should match pill `count` when fully drilled in. */
export function countBrowseSelectionProducts(
  catalog: ShopCatalogProduct[],
  selection: ShopBrowseSelection,
): number {
  if (!browseSelectionIsActive(selection)) return catalog.length;
  return catalog.filter((p) => productMatchesBrowseSelection(p, selection))
    .length;
}

export function findBrowseNavCount(
  index: ShopBrowseIndex,
  selection: ShopBrowseSelection,
): number | null {
  const { primarySlug, secondarySlug, subSlug, brandSlug } =
    coerceShopBrowseSelection(selection);

  if (brandSlug && primarySlug && secondarySlug && subSlug) {
    const pathKey = `${primarySlug}|${secondarySlug}|${subSlug}`;
    return (
      index.brandsByPathSlug[pathKey]?.find((b) => b.slug === brandSlug)
        ?.count ?? null
    );
  }
  if (subSlug && primarySlug && secondarySlug) {
    const pathKey = `${primarySlug}|${secondarySlug}`;
    return (
      index.subsByPathSlug[pathKey]?.find((s) => s.slug === subSlug)?.count ??
      null
    );
  }
  if (secondarySlug && primarySlug) {
    return (
      index.secondariesByPrimarySlug[primarySlug]?.find(
        (s) => s.slug === secondarySlug,
      )?.count ?? null
    );
  }
  if (primarySlug) {
    return index.primaries.find((p) => p.slug === primarySlug)?.count ?? null;
  }
  return null;
}

export function browseSelectionIsActive(selection: ShopBrowseSelection): boolean {
  return Boolean(
    selection.primarySlug?.trim() ||
      selection.secondarySlug?.trim() ||
      selection.subSlug?.trim() ||
      selection.brandSlug?.trim() ||
      (selection.brandSlugs || []).some((s) => String(s || "").trim()),
  );
}

/**
 * Minimum products a brand must have in the catalog before its name is shown
 * on a product card. Threshold is TBD with product — 3 hides one-off rows
 * without dropping real brand shelves.
 */
export const SHOP_BRAND_LABEL_MIN_COUNT = 3;

export function productBrandKey(product: ShopCatalogProduct): string {
  const slug = String(product.breadcrumbs?.brandSlug ?? "")
    .trim()
    .toLowerCase();
  if (slug) return slug;
  return slugifyBreadcrumbLabel(product.breadcrumbs?.brand ?? "");
}

export function countProductsByBrand(
  products: ShopCatalogProduct[],
): Map<string, number> {
  const counts = new Map<string, number>();
  for (const product of products) {
    const key = productBrandKey(product);
    if (!key) continue;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

/** True when this product's brand should render on the card. */
export function shouldShowProductBrand(
  product: ShopCatalogProduct,
  brandCounts: Map<string, number>,
  minCount: number = SHOP_BRAND_LABEL_MIN_COUNT,
): boolean {
  const brand = String(product.breadcrumbs?.brand ?? "").trim();
  if (!brand) return false;
  const key = productBrandKey(product);
  if (!key) return false;
  // Counts not loaded yet — show the brand rather than flashing it away.
  if (brandCounts.size === 0) return true;
  return (brandCounts.get(key) ?? 0) >= minCount;
}
